import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';

/**
 * Reusable assessment-parameter editor used in SOP create/edit dialogs.
 * Props:
 *   params   – array of { name, weight, description }
 *   onAdd    – () => void
 *   onUpdate – (idx, field, value) => void
 *   onRemove – (idx) => void
 */
function ParameterEditor({ params, onAdd, onUpdate, onRemove }) {
  const total = params.reduce((s, p) => s + Number(p.weight || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>Assessment Parameters (Weightage)</Label>
        <span className={`text-sm font-medium ${total === 100 ? 'text-emerald-600' : 'text-rose-600'}`}>
          Total: {total}%
        </span>
      </div>

      <div className="space-y-3 mt-2">
        {params.map((param, idx) => (
          <div key={idx} className="p-3 border border-slate-200 rounded-lg space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Parameter name (e.g., Greeting)"
                value={param.name}
                onChange={(e) => onUpdate(idx, 'name', e.target.value)}
                className="flex-1"
                data-testid={`param-name-${idx}`}
              />
              <Input
                type="number"
                placeholder="Weight %"
                value={param.weight}
                onChange={(e) => onUpdate(idx, 'weight', e.target.value)}
                className="w-24"
                min="0"
                max="100"
                data-testid={`param-weight-${idx}`}
              />
              {params.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onRemove(idx)}
                  data-testid={`remove-param-${idx}`}
                >
                  Remove
                </Button>
              )}
            </div>
            <Input
              placeholder="Description (optional)"
              value={param.description}
              onChange={(e) => onUpdate(idx, 'description', e.target.value)}
              data-testid={`param-desc-${idx}`}
            />
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onAdd}
        className="mt-2"
        data-testid="add-parameter-button"
      >
        <Plus size={14} className="mr-1" />Add Parameter
      </Button>
    </div>
  );
}

export default ParameterEditor;
